import { getImpersonationProps, impersonationIdKey } from 'utils/impersonation';

import type { EnvType } from './types';

type AuthClientHeaders = {
  Authorization?: string;
};

type ImpersonationHeaders = {
  'X-Dashboard-Impersonation-Id'?: string;
};

type CallbackFunc = (response: any) => void;

export const AuthenticationRequiredException = new Error('AuthenticationRequiredException');

export class BaseApiClient {
  private token = '';
  private env: EnvType = 'sandbox';

  private callbackOn401: CallbackFunc | null = null;

  constructor(private apiRoot: string) {}

  setAuthToken(token: string) {
    this.token = token;
  }

  setEnv(env: EnvType) {
    this.env = env;
  }

  setCallbackOn401(callback: CallbackFunc) {
    this.callbackOn401 = callback;
  }

  protected getAuthHeaders(): AuthClientHeaders {
    if (this.token) {
      return {
        Authorization: `Bearer ${this.token}`,
      };
    }

    return {};
  }

  protected getImpersonationHeaders = (): ImpersonationHeaders => {
    const impersonationId = getImpersonationProps(impersonationIdKey);

    if (impersonationId) {
      return {
        'X-Dashboard-Impersonation-Id': impersonationId,
      };
    }

    return {};
  };

  protected getEnvHeaders = (): Record<string, string> => {
    return { 'X-Dashboard-Key-Env': this.env };
  };

  protected getHeaders(): Record<string, string> {
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
      ...this.getImpersonationHeaders(),
      ...this.getEnvHeaders(),
    };
  }

  protected getDownloadHeaders(accept: 'application/pdf'): Record<string, string> {
    return {
      Accept: accept,
      ...this.getAuthHeaders(),
      ...this.getImpersonationHeaders(),
      ...this.getEnvHeaders(),
    };
  }

  protected getAbsoluteUrl = (url: string) => {
    if (url.startsWith('http')) {
      return url;
    }

    return `${this.apiRoot}${url}`;
  };

  request<T>(url: string, options?: Partial<RequestInit>, params?: any): Promise<T> {
    const u = new URL(this.getAbsoluteUrl(url));

    // TODO: just naive implementation
    Object.keys(params || {})
      .filter((key) => params[key] !== null && params[key] !== undefined)
      .forEach((key) => {
        const currentValue = params[key];

        if (Array.isArray(currentValue)) {
          currentValue.forEach((val) => u.searchParams.append(key, val));
        } else {
          u.searchParams.append(key, currentValue);
        }
      });

    function isOwnServer(lastUrl: string, apiRoot: string) {
      return lastUrl.startsWith(apiRoot);
    }

    return fetch(u.href, {
      credentials: 'omit',
      ...options,
    }).then((resp) => {
      if (resp.status === 204) {
        return null;
      }

      if (resp.status < 299 && resp.status >= 200) {
        if (params && params.responseType === 'blob') {
          return resp.blob();
        }

        return resp.json();
      }

      //session has expired
      if (resp.status === 401 && isOwnServer(u.toString(), this.apiRoot)) {
        if (this.callbackOn401) {
          const callback = this.callbackOn401;

          return resp.json().then((data) => {
            callback(data);
            throw data;
          });
        } else {
          console.warn('use `this.callbackOn401` for general callback on 401');
          throw AuthenticationRequiredException;
        }
      }

      return resp.json().then((data) => {
        throw data;
      });
    });
  }

  patch<T, K = FormData | Record<string, unknown>>(url: string, data: K): Promise<T> {
    const headers = this.getHeaders();

    let body: string | FormData = '';

    if (data instanceof FormData) {
      body = data;
      delete headers['Content-Type'];
    } else if (data) {
      body = JSON.stringify(data);
    } else {
      delete headers['Content-Type'];
    }

    return this.request<T>(url, {
      headers,
      body,
      method: 'PATCH',
    });
  }

  delete<T>(url: string, data: any = undefined): Promise<T> {
    let body;

    if (data) {
      body = JSON.stringify(data);
    }

    return this.request<T>(url, {
      headers: this.getHeaders(),
      method: 'DELETE',
      body,
    });
  }

  post<T, K = FormData | Record<string, unknown>>(url: string, data: K): Promise<T> {
    const headers = this.getHeaders();

    let body: string | FormData = '';

    if (data instanceof FormData) {
      body = data;
      delete headers['Content-Type'];
    } else if (data) {
      body = JSON.stringify(data);
    } else {
      delete headers['Content-Type'];
    }

    return this.request<T>(url, {
      headers,
      body,
      method: 'POST',
    });
  }

  get<T>(url: string, params?: unknown, options?: Partial<RequestInit>): Promise<T> {
    return this.request(
      url,
      {
        headers: this.getHeaders(),
        method: 'get',
        ...options,
      },
      params,
    );
  }

  download<T>(url: string): Promise<T> {
    return this.request(
      url,
      {
        headers: this.getDownloadHeaders('application/pdf'),
        method: 'get',
      },
      { responseType: 'blob' },
    );
  }
}

export const baseApiClient = new BaseApiClient(import.meta.env.VITE_APP_API_URL ?? '');
